import { useCallback, useEffect, useState } from "react";
import {
    Button, Dialog, DialogActions, DialogContent, DialogTitle,
    Stepper, Step, StepLabel, Box,
    Autocomplete, TextField, IconButton, Typography,
    Divider, Chip, Card, CardContent, Tooltip,
    LinearProgress, Fade, CircularProgress,
    Stack, Avatar, List, ListItem, ListItemText, ListItemAvatar,
    Zoom, InputAdornment, Paper, FormControl, InputLabel, Select, MenuItem, Popover, Badge
} from "@mui/material";

import axios from 'axios';
import {
    AddSharp, Close, Cancel as CancelIcon,
    Groups, Assignment, Preview, CheckCircle, PersonAdd,
    Work, Business, Build, Notes, Clear as ClearIcon,
    PlayArrow, Done, Settings, PrecisionManufacturing, Refresh
} from "@mui/icons-material";
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from "prop-types";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const UPS_OPTIONS = Array.from({ length: 50 }, (_, index) => index + 1);

const JobAddButton = ({ supervisorId, machineCode, groupCode, onAddJob, onTriggerSnackbar }) => {
    const isBreakingGroup = String(groupCode || '').trim().toUpperCase() === 'BRT';
    const [lineupDialogOpen, setLineupDialogOpen] = useState(false);
    const [fetchingLineupJobs, setFetchingLineupJobs] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [employeeData, setEmployeeData] = useState([]);
    const [groups, setGroups] = useState([{ id: 1, selectedEmployees: [] }]);
    const [employeeJobSelections, setEmployeeJobSelections] = useState({});
    const [categorizedJobs, setCategorizedJobs] = useState([]);
    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [manualEntryDialogOpen, setManualEntryDialogOpen] = useState(false);
    const [manualEntryData, setManualEntryData] = useState({
        jobNumber: '', customer: '', product: '', mainOperation: '', reason: ''
    });
    const [manualEntryErrors, setManualEntryErrors] = useState({});
    const [disabledButtons, setDisabledButtons] = useState({});
    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [fetchingEmployees, setFetchingEmployees] = useState(false);
    const [fetchingJobs, setFetchingJobs] = useState(false);


    const [editAnchorEl, setEditAnchorEl] = useState(null);
    const [editGroupId, setEditGroupId] = useState(null);
    const [tempMainOperation, setTempMainOperation] = useState('');
    const [upsAnchorEl, setUpsAnchorEl] = useState(null);
    const [upsGroupId, setUpsGroupId] = useState(null);

    // ADD these new state variables after line ~15 where other states are declared
    const [selectedJob, setSelectedJob] = useState(null);
    const [mainOps, setMainOps] = useState([{ value: 'default', label: 'Default (From Job Data)' }]);
    const [loadingMainOps, setLoadingMainOps] = useState(false);

    const loadMainOps = useCallback(async () => {
        if (!machineCode) return;
        setLoadingMainOps(true);
        try {
            const { data } = await axios.get(`${API_BASE_URL}/main-ops/by-machine`, {
                params: { machine_code: machineCode },
            });
            setMainOps(data?.options?.length ? data.options : [{ value: 'default', label: 'Default (From Job Data)' }]);
        } catch (e) {
            onTriggerSnackbar?.('Main operations list unavailable. Using defaults.', 'warning');
            setMainOps([{ value: 'default', label: 'Default (From Job Data)' }]);
        } finally {
            setLoadingMainOps(false);
        }
    }, [machineCode, onTriggerSnackbar]); // <- deps the function really uses

    useEffect(() => {
        loadMainOps();
    }, [loadMainOps]); // <- depend on the memoized function




    const closeEditMainOperation = () => {
        setEditAnchorEl(null);
        setEditGroupId(null);
        setTempMainOperation('');
    };


    const openUpsPopover = (event, groupId) => {
        setUpsGroupId(groupId);
        setUpsAnchorEl(event.currentTarget);
    };

    const closeUpsPopover = () => {
        setUpsAnchorEl(null);
        setUpsGroupId(null);
    };

    const selectUpCount = (value) => {
        if (!upsGroupId) return;

        setEmployeeJobSelections(prev => ({
            ...prev,
            [upsGroupId]: {
                ...prev[upsGroupId],
                upCount: Number(value),
            },
        }));
        closeUpsPopover();
    };


    const saveEditMainOperation = () => {
        if (!editGroupId) return;

        setEmployeeJobSelections(prev => {
            const cur = prev[editGroupId] || {};
            const original =
                cur.mainOperationFromJob ??
                cur.mainOperation ??
                '';

            return {
                ...prev,
                [editGroupId]: {
                    ...cur,
                    mainOperation: (tempMainOperation === 'default')
                        ? (original || '')
                        : tempMainOperation,
                    mainOperationFromJob: original,
                }
            };
        });

        closeEditMainOperation();
    };



    // REPLACE openEditMainOperation with this:
    const openEditMainOperation = (event, groupId) => {
        const data = employeeJobSelections[groupId];
        const current = data?.mainOperation || '';
        const isDefault = data?.mainOperationFromJob && current === data.mainOperationFromJob;

        setTempMainOperation(isDefault ? 'default' : (current || 'default'));
        setEditGroupId(groupId);
        setEditAnchorEl(event.currentTarget);
    };



    const steps = [
        { label: 'Add Employees', icon: <Groups />, description: 'Group your employees for different tasks' },
        { label: 'Assign Jobs', icon: <Assignment />, description: 'Select jobs for each employee group' },
        { label: 'Review & Confirm', icon: <Preview />, description: 'Review assignments before starting' }
    ];



    const fetchLineupJobs = async () => {
        if (!machineCode) {
            console.warn("Machine code is empty, skipping fetch.");
            return;
        }

        // Console log the machine code being sent to backend
        console.log("🔧 Fetching lineup jobs for machine code:", machineCode);

        try {
            const response = await axios.get(`${API_BASE_URL}/fetchLineupJobsForGroup`, {
                params: { machineCode }
            });

            // Optional: Log the API call details
            console.log("📡 API Request URL:", `${API_BASE_URL}/fetchLineupJobsForGroup?machineCode=${machineCode}`);
            console.log("📦 Lineup jobs response:", response.data);

            const categorized = response.data.map(job => ({
                label: `Seq ${job.jobSequenceNumber}: ${job.jobNumber} - ${job.customer || 'No Customer'}`,
                job: {
                    ...job,
                    // Map lineup job fields to standard job fields for compatibility
                    jobNumber: job.jobNumber || 'N/A',
                    customer: job.customer || 'No Customer',
                    WOdescription: job.WOdescription || 'N/A',
                    OpDesctiption: job.OpDesctiption || 'N/A',
                    plannedQty: job.plannedQty || job.OPQty || 0,
                    OperationalTime: job.OperationalTime || 0,
                    SetupAndOpeTime: job.SetupAndOpeTime || 0,
                    SetupTime: job.SetupTime || 0,
                    jobSequenceNumber: job.jobSequenceNumber,
                    WOpriority: job.WOpriority,
                    WOQty: job.WOQty,
                    OPQty: job.OPQty,
                    ColorSequence: job.ColorSequence,
                    OtherDetails: job.OtherDetails,
                    TimeUnit: job.TimeUnit,
                    WOduedate: job.WOduedate,
                    WOenddate: job.WOenddate,
                    WOdelay: job.WOdelay
                }
            }));

            // Sort by job sequence number (lineup order)
            categorized.sort((a, b) => {
                const seqA = parseInt(a.job.jobSequenceNumber) || 0;
                const seqB = parseInt(b.job.jobSequenceNumber) || 0;
                return seqA - seqB;
            });

            console.log("✅ Processed lineup jobs:", categorized.length, "jobs found");
            setCategorizedJobs(categorized);
        } catch (err) {
            console.error("❌ Failed to fetch lineup jobs for machine:", machineCode, err);
            onTriggerSnackbar('Failed to fetch lineup jobs. Please try again.', 'error');
        }
    };


    const handleOpenLineupDialog = (groupId) => {
        setSelectedGroupId(groupId);
        setSelectedJob(null);
        setLineupDialogOpen(true);

        // Optional: Auto-fetch fresh data when dialog opens
        setTimeout(async () => {
            if (!fetchingLineupJobs) {
                setFetchingLineupJobs(true);
                try {
                    await fetchLineupJobs();
                } catch (error) {
                    console.error("Error fetching lineup jobs:", error);
                } finally {
                    setFetchingLineupJobs(false);
                }
            }
        }, 300); // Small delay for smooth animation
    };


    const handleCloseLineupDialog = () => {
        setLineupDialogOpen(false);
        setSelectedJob(null);
        // Optional: Clear cached data so next open is fresh
        setCategorizedJobs([]);
    };

    // Apple-style Dialog Header Component
    const AppleDialogHeader = ({ title, icon: Icon, onClose, subtitle, color = 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)' }) => (
        <DialogTitle sx={{
            p: 0,
            background: color,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.05) 100%)',
                pointerEvents: 'none',
            }
        }}>
            {/* Top bar with traffic lights */}
            <Box sx={{
                height: '32px',
                background: 'rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pl: 2,
                pr: 2,
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Box sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #ff5f57 0%, #ff3b30 100%)',
                        cursor: 'pointer',
                        '&:hover': { transform: 'scale(1.1)' },
                        transition: 'transform 0.2s ease',
                    }}
                        onClick={onClose}
                    />
                    <Box sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #ffbd2e 0%, #ff9500 100%)',
                        cursor: 'pointer',
                        '&:hover': { transform: 'scale(1.1)' },
                        transition: 'transform 0.2s ease',
                    }} />
                    <Box sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #28cd41 0%, #30d158 100%)',
                        cursor: 'pointer',
                        '&:hover': { transform: 'scale(1.1)' },
                        transition: 'transform 0.2s ease',
                    }} />
                </Box>
                <IconButton
                    onClick={onClose}
                    sx={{
                        color: 'rgba(255, 255, 255, 0.8)',
                        '&:hover': { color: 'white', backgroundColor: 'rgba(255, 255, 255, 0.1)' }
                    }}
                    size="small"
                >
                    <Close fontSize="small" />
                </IconButton>
            </Box>

            {/* Main header content */}
            <Box sx={{
                p: 1.5,
                pb: 2,
                position: 'relative',
                zIndex: 1,
                textAlign: 'center',
            }}>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <Box sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '16px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}>
                        <Icon sx={{
                            fontSize: 28,
                            color: 'white',
                            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                        }} />
                    </Box>
                </Box>

                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 700,
                        color: 'white',
                        fontSize: '28px',
                        letterSpacing: '-0.5px',
                        lineHeight: 1.2,
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        mb: 1,
                    }}
                >
                    {title}
                </Typography>

                {subtitle && (
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '16px',
                            fontWeight: 500,
                            lineHeight: 1.4,
                        }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </DialogTitle>
    );

    // Apple-style Button Component
    const AppleButton = ({ variant = 'contained', color = 'primary', children, onClick, disabled, startIcon, sx, size = 'medium', ...props }) => {
        const getButtonStyles = () => {
            const baseStyles = {
                borderRadius: '12px',
                fontSize: size === 'large' ? '16px' : '14px',
                fontWeight: 600,
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                textTransform: 'none',
                border: 'none',
                px: size === 'large' ? 4 : 3,
                py: size === 'large' ? 1.5 : 1,
            };

            if (variant === 'contained') {
                switch (color) {
                    case 'primary':
                        return {
                            ...baseStyles,
                            background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                            color: 'white',
                            boxShadow: '0 4px 16px rgba(0, 122, 255, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0056cc 0%, #4c44cc 100%)',
                                boxShadow: '0 6px 20px rgba(0, 122, 255, 0.4)',

                            },
                        };
                    case 'success':
                        return {
                            ...baseStyles,
                            background: 'linear-gradient(135deg, #34c759 0%, #30d158 100%)',
                            color: 'white',
                            boxShadow: '0 4px 16px rgba(52, 199, 89, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #2a9d47 0%, #28a745 100%)',
                                boxShadow: '0 6px 20px rgba(52, 199, 89, 0.4)',

                            },
                        };
                    case 'error':
                        return {
                            ...baseStyles,
                            background: 'linear-gradient(135deg, #ff3b30 0%, #ff5f57 100%)',
                            color: 'white',
                            boxShadow: '0 4px 16px rgba(255, 59, 48, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #d70015 0%, #e53935 100%)',
                                boxShadow: '0 6px 20px rgba(255, 59, 48, 0.4)',

                            },
                        };
                    case 'warning':
                        return {
                            ...baseStyles,
                            background: 'linear-gradient(135deg, #ff9500 0%, #ffbd2e 100%)',
                            color: 'white',
                            boxShadow: '0 4px 16px rgba(255, 149, 0, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #e6840e 0%, #f4a20c 100%)',
                                boxShadow: '0 6px 20px rgba(255, 149, 0, 0.4)',

                            },
                        };
                    default:
                        return {
                            ...baseStyles,
                            background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                            color: 'white',
                            boxShadow: '0 4px 16px rgba(0, 122, 255, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0056cc 0%, #4c44cc 100%)',
                                boxShadow: '0 6px 20px rgba(0, 122, 255, 0.4)',

                            },
                        };
                }
            } else if (variant === 'outlined') {
                return {
                    ...baseStyles,
                    borderColor: 'transparent',
                    backgroundColor: 'rgba(142, 142, 147, 0.12)',
                    color: '#007aff',
                    '&:hover': {
                        backgroundColor: 'rgba(142, 142, 147, 0.2)',
                        borderColor: 'transparent',
                        transform: 'scale(1.02)',
                    },
                };
            }
        };

        return (
            <Button
                variant={variant}
                onClick={onClick}
                disabled={disabled}
                startIcon={startIcon}
                sx={{
                    ...getButtonStyles(),
                    '&:disabled': {
                        background: 'rgba(142, 142, 147, 0.3)',
                        color: 'rgba(255, 255, 255, 0.6)',
                    },
                    ...sx,
                }}
                {...props}
            >
                {children}
            </Button>
        );
    };

    // Job number formatting function
    const formatJobNumber = (value) => {
        const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        let formatted = '';
        let count = 0;

        for (let i = 0; i < cleanValue.length && count < 10; i++) {
            const char = cleanValue[i];

            if (count < 4) {
                if (/\d/.test(char)) {
                    formatted += char;
                    count++;
                    if (count === 2 || count === 4) formatted += '-';
                }
            } else if (count < 6) {
                if (/\d/.test(char)) {
                    formatted += char;
                    count++;
                }
            } else {
                if (/[A-Z0-9]/.test(char)) {
                    formatted += char;
                    count++;
                }
            }
        }

        return formatted;
    };

    // Validation function for manual entry fields
    const validateManualEntryField = (fieldName, value) => {
        const newErrors = { ...manualEntryErrors };

        if (fieldName === 'jobNumber') {
            if (value.trim() === '') {
                newErrors[fieldName] = 'Job Number is required';
            } else if (value.length < 7) {
                newErrors[fieldName] = 'Job number must follow the pattern: NN-NN-XXXX';
            } else {
                delete newErrors[fieldName];
            }
        } else if (value.trim() === '') {
            newErrors[fieldName] = `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
        } else {
            delete newErrors[fieldName];
        }

        setManualEntryErrors(newErrors);
    };

    const handleJobNumberChange = (e) => {
        const inputValue = e.target.value;
        const formattedValue = formatJobNumber(inputValue);
        setManualEntryData(prevData => ({ ...prevData, jobNumber: formattedValue }));
        // Validation removed - will happen onBlur
    };

    // Clear job number function
    const clearJobNumber = () => {
        setManualEntryData(prevData => ({ ...prevData, jobNumber: '' }));
        setManualEntryErrors(prev => ({ ...prev, jobNumber: 'Job Number is required' }));  // ✅ ADD THIS
    };

    // Check if manual entry is valid
    const isManualEntryValid = () => {
        const { jobNumber, customer, product, mainOperation, reason } = manualEntryData;
        return (
            jobNumber.trim() !== '' &&
            customer.trim() !== '' &&
            product.trim() !== '' &&
            mainOperation.trim() !== '' &&
            reason.trim() !== '' &&
            jobNumber.length >= 7 &&
            Object.keys(manualEntryErrors).length === 0
        );
    };

    const handleReset = () => {
        setActiveStep(0);
        setGroups([{ id: 1, selectedEmployees: [] }]);
        setEmployeeJobSelections({});
        setDisabledButtons({});
        setManualEntryData({
            jobNumber: '', customer: '', product: '', mainOperation: '', reason: '',
        });
        setManualEntryErrors({});
        setCategorizedJobs([]);
        setCategorizedJobs([]);
        setEmployeeData([]);
        setSelectedGroupId(null);
        setSelectedJob(null);        // ADD this line
        setLineupDialogOpen(false);
    };

    const handleCloseDialog = () => {
        setActiveStep(0);
        setGroups([{ id: 1, selectedEmployees: [] }]);
        setEmployeeJobSelections({});
        setDisabledButtons({});
        setManualEntryData({
            jobNumber: '', customer: '', product: '', mainOperation: '', reason: '',
        });
        setManualEntryErrors({});
        setCategorizedJobs([]);
        setCategorizedJobs([]);
        setEmployeeData([]);
        setSelectedGroupId(null);
        setSelectedJob(null);        // ADD this line
        setDialogOpen(false);
        setLineupDialogOpen(false);
    };

    // REPLACE the existing handleClosePlanDialog function with this updated version
    const handleClosePlanDialog = () => {
        setPlanDialogOpen(false);
        setSelectedJob(null);        // ADD this line
    };

    const handleCloseManualEntryDialog = () => {
        setManualEntryDialogOpen(false);
        setManualEntryData({
            jobNumber: '', customer: '', product: '', mainOperation: '', reason: ''
        });
        setManualEntryErrors({});
    };

    const handleOpenPlanDialog = (groupId) => {
        setSelectedGroupId(groupId);
        setSelectedJob(null);        // ADD this line
        setPlanDialogOpen(true);
    };

    const handleOpenManualEntryDialog = (groupId) => {
        setSelectedGroupId(groupId);
        setManualEntryDialogOpen(true);
    };


    const handleConfirmJobData = (groupId, jobData) => {
        const originalFromJob = jobData?.OpDesctiption ?? jobData?.mainOperation ?? '';

        const normalized = {
            jobNumber: jobData?.jobNumber ?? '',
            customer: jobData?.customer ?? '',
            product: jobData?.product ?? jobData?.WOdescription ?? '',
            mainOperation: jobData?.mainOperation ?? jobData?.OpDesctiption ?? '',
            mainOperationFromJob: originalFromJob,
            upCount: jobData?.upCount ?? '',
        };

        setEmployeeJobSelections((prev) => ({
            ...prev,
            [groupId]: normalized,
        }));
        setDisabledButtons((prev) => ({ ...prev, [groupId]: true }));

        handleClosePlanDialog();
        handleCloseManualEntryDialog();
        handleCloseLineupDialog();
    };


    const handleManualEntryChange = (field, value) => {
        if (field === 'jobNumber') {
            const formattedValue = formatJobNumber(value);
            setManualEntryData(prevData => ({ ...prevData, jobNumber: formattedValue }));
            // Validation removed - will happen onBlur
        } else {
            setManualEntryData((prevData) => ({ ...prevData, [field]: value }));
            // Validation removed - will happen onBlur
        }
    };

    const handleSubmitManualEntry = () => {
        const fields = ['jobNumber', 'customer', 'product', 'mainOperation', 'reason'];
        let hasErrors = false;

        fields.forEach(field => {
            validateManualEntryField(field, manualEntryData[field]);
            if (manualEntryData[field].trim() === '' || (field === 'jobNumber' && manualEntryData[field].length < 7)) {
                hasErrors = true;
            }
        });

        if (!hasErrors && Object.keys(manualEntryErrors).length === 0) {
            const groupId = selectedGroupId;
            handleConfirmJobData(groupId, manualEntryData);
            setManualEntryData({
                jobNumber: '', customer: '', product: '', mainOperation: '', reason: '',
            });
            setManualEntryErrors({});
        }
    };

    const handleConfirm = async () => {
        if (
            isBreakingGroup &&
            groups.some(group => {
                const upCount = Number(employeeJobSelections[group.id]?.upCount);
                return !Number.isInteger(upCount) || upCount < 1 || upCount > 50;
            })
        ) {
            onTriggerSnackbar('Please select the Number of Ups (1–50) for each Breaking job.', 'warning');
            return;
        }

        setSubmitting(true);
        const startDatetime = new Date().toISOString();
        const activeStatus = 'ongoing';

        const payload = groups.flatMap(group => {
            const selectedJob = employeeJobSelections[group.id];
            return group.selectedEmployees.map(employee => ({
                supervisor_id: supervisorId,
                emp_id: employee.emp_id,
                emp_name: employee.emp_name,
                station_code: machineCode,
                job_number: selectedJob?.jobNumber || '',
                customer: selectedJob?.customer || '',
                product: selectedJob?.product || '',
                main_operation: selectedJob?.mainOperation || '',
                up_count: isBreakingGroup ? selectedJob?.upCount : null,
                operation_description: 'JOB_STARTED',
                start_datetime: startDatetime,
                active_status: activeStatus,
            }));
        });

        try {
            const response = await axios.post(`${API_BASE_URL}/ManhourJobStart`, payload);
            if (response.status === 200) {
                onTriggerSnackbar('🎉 Jobs started successfully!', 'success');
                if (onAddJob) {
                    onAddJob(response.data.jobStartData);
                }
                handleCloseDialog();
            } else {
                onTriggerSnackbar('Error starting jobs. Please try again.', 'error');
            }
        } catch (error) {
            onTriggerSnackbar('Error starting jobs. Please check your connection.', 'error');
            console.error("Error sending data:", error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddToJobClick = async () => {
        setLoading(true);
        try {

            setDialogOpen(true);
        } catch (error) {
            console.error("Error fetching job data:", error);
            onTriggerSnackbar('Failed to load job data. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployeeData = async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/getLoginEmployees`, {
                params: { supervisor_id: supervisorId, station_code: machineCode }
            });

            if (response.data.length === 0) {
                onTriggerSnackbar('No available employees found for this station.', 'info');
                return [];
            } else {
                setEmployeeData(response.data);
                return response.data;
            }
        } catch (error) {
            console.error("Error fetching employee data:", error);
            onTriggerSnackbar('Failed to load employee data. Please try again.', 'error');
            return [];
        }
    };

    const fetchJobData = async () => {
        try {
            if (machineCode.startsWith("QCTW")) {
                const date = new Date().toISOString().slice(0, 10);
                const response = await axios.get(`${API_BASE_URL}/getQCMCompletedJobs`, {
                    params: { date }
                });

                const jobs = response.data.map(job => ({
                    jobNumber: job.jobNo,
                    customer: job.customer,
                    WOdescription: job.product,
                    OpDesctiption: job.mainOperation,
                    startDatetime: job.startDatetime,
                    endDatetime: job.endDatetime,
                    machineCode: job.machineCode
                }));

                categorizeJobs(jobs);
            } else {
                const response = await axios.get(`${API_BASE_URL}/OperatorViewPlan`, {
                    params: { machineCode, dateTime: new Date().toISOString() }
                });
                categorizeJobs(response.data);
            }
        } catch (error) {
            onTriggerSnackbar('Failed to load job data. Please try again.', 'error');
        }
    };

    const categorizeJobs = (jobs) => {
        const categorized = jobs.map((job) => ({
            label: job.planningFrom && job.planingTo
                ? `${adjustAndFormatDate(job.planningFrom)} - ${adjustAndFormatDate(job.planingTo)}`
                : "No Date",
            job: job || {},
        }));
        categorized.sort((a, b) => a.label.localeCompare(b.label));
        setCategorizedJobs(categorized);
    };

    const adjustAndFormatDate = (dateString) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat("en-US", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit", hour12: true,
        }).format(date);
    };

    const handleNext = () => {
        if (activeStep === 0 && groups.some(group => group.selectedEmployees.length === 0)) {
            onTriggerSnackbar('Please add at least one employee to each group.', 'warning');
            return;
        }
        if (activeStep === 1 && Object.keys(employeeJobSelections).length < groups.length) {
            onTriggerSnackbar('Please assign a job to each group.', 'warning');
            return;
        }
        if (
            activeStep === 1 &&
            isBreakingGroup &&
            groups.some(group => {
                const upCount = Number(employeeJobSelections[group.id]?.upCount);
                return !Number.isInteger(upCount) || upCount < 1 || upCount > 50;
            })
        ) {
            onTriggerSnackbar('Please select the Number of Ups (1–50) for each Breaking job.', 'warning');
            return;
        }
        setActiveStep((prevStep) => prevStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const handleAddGroup = () => {
        const allSelected = groups.flatMap(group => group.selectedEmployees.map(e => e.emp_id));
        const unselectedEmployees = employeeData.filter(emp => !allSelected.includes(emp.emp_id));

        if (unselectedEmployees.length === 0) {
            onTriggerSnackbar('All employees have been assigned to groups.', 'info');
            return;
        }

        setGroups([...groups, { id: groups.length + 1, selectedEmployees: [] }]);
    };


    // UPDATE handleEmployeeJobSelect
    const handleEmployeeJobSelect = (groupId, job) => {
        setSelectedJob(job);
    };





    const handleRemoveGroup = (groupId) => {
        setGroups(prevGroups => prevGroups.filter(group => group.id !== groupId));
    };

    const renderStepContent = (step) => {
        switch (step) {


            case 0:
                return (
                    <Fade in timeout={500}>
                        <Box sx={{ width: '100%' }}>
                            <Stack spacing={3}>
                                <AnimatePresence>
                                    {groups.map((group, index) => {
                                        const allSelected = groups.flatMap(g => g.selectedEmployees.map(e => e.emp_id));
                                        const unselectedEmployees = employeeData.filter(emp =>
                                            !allSelected.includes(emp.emp_id) ||
                                            group.selectedEmployees.some(e => e.emp_id === emp.emp_id)
                                        );

                                        return (
                                            <motion.div
                                                key={group.id}
                                                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: -20, scale: 0.95 }}
                                                transition={{
                                                    duration: 0.4,
                                                    delay: index * 0.1,
                                                    type: "spring",
                                                    stiffness: 300,
                                                    damping: 25
                                                }}
                                            >
                                                <Card
                                                    elevation={0}
                                                    sx={{
                                                        borderRadius: '20px',
                                                        background: 'rgba(255, 255, 255, 0.9)',
                                                        backdropFilter: 'blur(20px)',
                                                        border: '2px solid rgba(0, 122, 255, 0.1)',
                                                        transition: 'all 0.3s ease',
                                                        '&:hover': {
                                                            border: '2px solid rgba(0, 122, 255, 0.2)',

                                                            boxShadow: '0 8px 25px rgba(0, 122, 255, 0.1)',
                                                        }
                                                    }}
                                                >
                                                    <CardContent sx={{ p: 3 }}>
                                                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                                            <Box display="flex" alignItems="center" gap={2}>
                                                                <Box sx={{
                                                                    width: 40,
                                                                    height: 40,
                                                                    borderRadius: '12px',
                                                                    background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    boxShadow: '0 4px 12px rgba(0, 122, 255, 0.25)',
                                                                }}>
                                                                    <Groups sx={{ color: 'white', fontSize: 20 }} />
                                                                </Box>
                                                                <Box>
                                                                    <Typography
                                                                        variant="h6"
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            color: '#1d1d1f',
                                                                            fontSize: '18px',
                                                                            letterSpacing: '-0.2px',
                                                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                                        }}
                                                                    >
                                                                        Group {group.id}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            color: '#8e8e93',
                                                                            fontSize: '14px',
                                                                            fontWeight: 500,
                                                                        }}
                                                                    >
                                                                        {group.selectedEmployees.length} member{group.selectedEmployees.length !== 1 ? 's' : ''}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                            {group.id !== 1 && (
                                                                <Tooltip title="Remove Group">
                                                                    <IconButton
                                                                        onClick={() => handleRemoveGroup(group.id)}
                                                                        sx={{
                                                                            color: '#8e8e93',
                                                                            backgroundColor: 'rgba(255, 59, 48, 0.1)',
                                                                            borderRadius: '8px',
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(255, 59, 48, 0.2)',
                                                                                color: '#ff3b30',
                                                                                transform: 'scale(1.05)',
                                                                            },
                                                                            transition: 'all 0.2s ease',
                                                                        }}
                                                                        size="small"
                                                                    >
                                                                        <Close fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </Box>

                                                        {/* Employee Selection Cards */}
                                                        {fetchingEmployees ? (
                                                            <Box sx={{
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center',
                                                                p: 4
                                                            }}>
                                                                <CircularProgress />
                                                                <Typography sx={{ ml: 2, color: '#8e8e93' }}>
                                                                    Loading employees...
                                                                </Typography>
                                                            </Box>
                                                        ) : unselectedEmployees.length === 0 && group.selectedEmployees.length === 0 ? (
                                                            <Box sx={{
                                                                p: 3,
                                                                borderRadius: '16px',
                                                                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(251, 191, 36, 0.12) 50%, rgba(252, 211, 77, 0.08) 100%)',
                                                                backdropFilter: 'blur(20px)',
                                                                border: '1px solid rgba(245, 158, 11, 0.15)',
                                                                textAlign: 'center',
                                                            }}>
                                                                <Typography variant="body2" sx={{
                                                                    color: '#1d1d1f',
                                                                    fontWeight: 500,
                                                                    fontSize: '16px',
                                                                }}>
                                                                    {employeeData.length === 0
                                                                        ? 'Click below to load available employees'
                                                                        : 'All employees have been assigned to groups'}
                                                                </Typography>
                                                            </Box>
                                                        ) : (
                                                            <Card sx={{
                                                                borderRadius: '16px',
                                                                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(248, 250, 252, 0.8) 50%, rgba(241, 245, 249, 0.7) 100%)',
                                                                backdropFilter: 'blur(25px)',
                                                                border: '1px solid rgba(255, 255, 255, 0.5)',
                                                                overflow: 'hidden',
                                                                maxHeight: '400px',
                                                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
                                                            }}>
                                                                <Box sx={{
                                                                    height: '100%',
                                                                    overflowY: 'auto',
                                                                    p: 2,
                                                                    '&::-webkit-scrollbar': { width: 8 },
                                                                    '&::-webkit-scrollbar-track': { background: 'rgba(255, 255, 255, 0.1)' },
                                                                    '&::-webkit-scrollbar-thumb': {
                                                                        background: 'rgba(0, 0, 0, 0.2)',
                                                                        borderRadius: 4
                                                                    },
                                                                }}>
                                                                    <Box sx={{
                                                                        display: 'grid',
                                                                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                                                        gap: 1.5,
                                                                    }}>
                                                                        {unselectedEmployees.map((employee) => {
                                                                            const isSelected = group.selectedEmployees.some(e => e.emp_id === employee.emp_id);

                                                                            return (
                                                                                <Box
                                                                                    key={employee.emp_id}
                                                                                    onClick={() => {
                                                                                        setGroups(prevGroups => prevGroups.map(g => {
                                                                                            if (g.id === group.id) {
                                                                                                if (isSelected) {
                                                                                                    return {
                                                                                                        ...g,
                                                                                                        selectedEmployees: g.selectedEmployees.filter(e => e.emp_id !== employee.emp_id)
                                                                                                    };
                                                                                                } else {
                                                                                                    return {
                                                                                                        ...g,
                                                                                                        selectedEmployees: [...g.selectedEmployees, employee]
                                                                                                    };
                                                                                                }
                                                                                            }
                                                                                            return g;
                                                                                        }));
                                                                                    }}
                                                                                    sx={{
                                                                                        borderRadius: '12px',
                                                                                        p: 1.5,
                                                                                        cursor: 'pointer',
                                                                                        background: isSelected
                                                                                            ? 'linear-gradient(135deg, rgba(0, 122, 255, 0.15) 0%, rgba(88, 86, 214, 0.1) 100%)'
                                                                                            : 'transparent',
                                                                                        backdropFilter: isSelected ? 'blur(10px)' : 'none',
                                                                                        border: isSelected
                                                                                            ? '2px solid rgba(0, 122, 255, 0.4)'
                                                                                            : '2px solid transparent',
                                                                                        '&:hover': {
                                                                                            background: isSelected
                                                                                                ? 'linear-gradient(135deg, rgba(0, 122, 255, 0.25) 0%, rgba(88, 86, 214, 0.2) 100%)'
                                                                                                : 'rgba(255, 255, 255, 0.5)',

                                                                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                                                                        },
                                                                                        transition: 'all 0.2s ease',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 1.5,
                                                                                    }}
                                                                                >
                                                                                    <Badge
                                                                                        overlap="circular"
                                                                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                                                        badgeContent={
                                                                                            isSelected ? (
                                                                                                <CheckCircle
                                                                                                    sx={{
                                                                                                        color: '#007aff',
                                                                                                        fontSize: 14,
                                                                                                        bgcolor: 'white',
                                                                                                        borderRadius: '50%'
                                                                                                    }}
                                                                                                />
                                                                                            ) : null
                                                                                        }
                                                                                    >
                                                                                        <Avatar
                                                                                            sx={{
                                                                                                bgcolor: isSelected ? '#007aff' : 'rgba(142, 142, 147, 0.3)',
                                                                                                color: 'white',
                                                                                                fontWeight: 600,
                                                                                                fontSize: '14px',
                                                                                                width: 40,
                                                                                                height: 40,
                                                                                            }}
                                                                                        >
                                                                                            {employee.emp_name.charAt(0)}
                                                                                        </Avatar>
                                                                                    </Badge>

                                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                        <Typography variant="body1" sx={{
                                                                                            fontWeight: isSelected ? 600 : 500,
                                                                                            color: '#1d1d1f',
                                                                                            fontSize: '14px',
                                                                                            overflow: 'hidden',
                                                                                            textOverflow: 'ellipsis',
                                                                                            whiteSpace: 'nowrap',
                                                                                            lineHeight: 1.3,
                                                                                        }}>
                                                                                            {employee.emp_name}
                                                                                        </Typography>
                                                                                        <Typography variant="body2" sx={{
                                                                                            color: '#8e8e93',
                                                                                            fontSize: '12px',
                                                                                            fontWeight: 500,
                                                                                            lineHeight: 1.2,
                                                                                        }}>
                                                                                            ID: {employee.emp_id}
                                                                                        </Typography>
                                                                                    </Box>

                                                                                    <Box sx={{
                                                                                        width: 20,
                                                                                        height: 20,
                                                                                        borderRadius: '50%',
                                                                                        border: `2px solid ${isSelected ? '#007aff' : 'rgba(142, 142, 147, 0.5)'}`,
                                                                                        backgroundColor: isSelected ? '#007aff' : 'transparent',
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        transition: 'all 0.2s ease',
                                                                                        flexShrink: 0,
                                                                                    }}>
                                                                                        {isSelected && (
                                                                                            <Box sx={{
                                                                                                width: 6,
                                                                                                height: 6,
                                                                                                borderRadius: '50%',
                                                                                                backgroundColor: 'white',
                                                                                            }} />
                                                                                        )}
                                                                                    </Box>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                </Box>
                                                            </Card>
                                                        )}

                                                        {/* Load Employees Button */}
                                                        {employeeData.length === 0 && !fetchingEmployees && (
                                                            <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                                                                <AppleButton
                                                                    onClick={async () => {
                                                                        setFetchingEmployees(true);
                                                                        await fetchEmployeeData();
                                                                        setFetchingEmployees(false);
                                                                    }}
                                                                    startIcon={<PersonAdd />}
                                                                >
                                                                    Load Available Employees
                                                                </AppleButton>
                                                            </Box>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </motion.div>
                                        );
                                    })}
                                </AnimatePresence>

                                <Box display="flex" justifyContent="center" sx={{ mt: 2 }}>
                                    <AppleButton
                                        onClick={handleAddGroup}
                                        startIcon={<PersonAdd />}
                                        variant="outlined"
                                        sx={{
                                            borderStyle: 'dashed',
                                            borderWidth: 2,
                                            borderColor: '#007aff',
                                            color: '#007aff',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 122, 255, 0.08)',
                                                borderColor: '#007aff',
                                            },
                                        }}
                                    >
                                        Add New Group
                                    </AppleButton>
                                </Box>
                            </Stack>
                        </Box>
                    </Fade>
                );




            case 1:
                return (
                    <Fade in timeout={500}>
                        <Box sx={{ width: '100%' }}>


                            <Stack spacing={3}>
                                <AnimatePresence>
                                    {groups.map((group, index) => (
                                        <motion.div
                                            key={group.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{
                                                duration: 0.3,
                                                delay: index * 0.1,
                                                type: "spring",
                                                stiffness: 300,
                                                damping: 25
                                            }}
                                        >
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    borderRadius: '20px',
                                                    background: 'rgba(255, 255, 255, 0.9)',
                                                    backdropFilter: 'blur(20px)',
                                                    border: employeeJobSelections[group.id]
                                                        ? '2px solid rgba(52, 199, 89, 0.3)'
                                                        : '2px solid rgba(255, 149, 0, 0.3)',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {

                                                        boxShadow: employeeJobSelections[group.id]
                                                            ? '0 8px 25px rgba(52, 199, 89, 0.15)'
                                                            : '0 8px 25px rgba(255, 149, 0, 0.15)',
                                                    }
                                                }}
                                            >
                                                <CardContent sx={{ p: 3 }}>
                                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                                        <Box sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: '12px',
                                                            background: employeeJobSelections[group.id]
                                                                ? 'linear-gradient(135deg, #34c759 0%, #30d158 100%)'
                                                                : 'linear-gradient(135deg, #ff9500 0%, #ffbd2e 100%)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: employeeJobSelections[group.id]
                                                                ? '0 4px 12px rgba(52, 199, 89, 0.25)'
                                                                : '0 4px 12px rgba(255, 149, 0, 0.25)',
                                                        }}>
                                                            {employeeJobSelections[group.id] ? (
                                                                <CheckCircle sx={{ color: 'white', fontSize: 20 }} />
                                                            ) : (
                                                                <Groups sx={{ color: 'white', fontSize: 20 }} />
                                                            )}
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color: '#1d1d1f',
                                                                    fontSize: '18px',
                                                                    letterSpacing: '-0.2px',
                                                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                                }}
                                                            >
                                                                Group {group.id}
                                                            </Typography>
                                                            <Chip
                                                                label={employeeJobSelections[group.id] ? 'Job Assigned' : 'Needs Job'}
                                                                size="small"
                                                                sx={{
                                                                    height: '24px',
                                                                    borderRadius: '6px',
                                                                    fontSize: '12px',
                                                                    fontWeight: 600,
                                                                    ...(employeeJobSelections[group.id] ? {
                                                                        backgroundColor: 'rgba(52, 199, 89, 0.1)',
                                                                        color: '#34c759',
                                                                        border: '1px solid rgba(52, 199, 89, 0.2)',
                                                                    } : {
                                                                        backgroundColor: 'rgba(255, 149, 0, 0.1)',
                                                                        color: '#ff9500',
                                                                        border: '1px solid rgba(255, 149, 0, 0.2)',
                                                                    }),
                                                                }}
                                                            />
                                                        </Box>
                                                    </Box>

                                                    <Box mb={2}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                color: '#8e8e93',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                                mb: 1,
                                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                            }}
                                                        >
                                                            Team Members:
                                                        </Typography>
                                                        <Box display="flex" flexWrap="wrap" gap={1}>
                                                            {group.selectedEmployees.map(emp => (
                                                                <Chip
                                                                    key={emp.emp_id}
                                                                    label={emp.emp_name}
                                                                    avatar={<Avatar sx={{ bgcolor: '#007aff', fontSize: '12px' }}>{emp.emp_name.charAt(0)}</Avatar>}
                                                                    size="small"
                                                                    sx={{
                                                                        height: '28px',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: 'rgba(0, 122, 255, 0.1)',
                                                                        color: '#007aff',
                                                                        border: '1px solid rgba(0, 122, 255, 0.2)',
                                                                        fontWeight: 500,
                                                                        fontSize: '12px',
                                                                    }}
                                                                />
                                                            ))}
                                                        </Box>
                                                    </Box>

                                                    {employeeJobSelections[group.id] ? (
                                                        <Paper
                                                            elevation={0}
                                                            sx={{
                                                                p: 3,
                                                                borderRadius: '16px',
                                                                background: 'linear-gradient(135deg, rgba(52, 199, 89, 0.08) 0%, rgba(48, 209, 88, 0.08) 100%)',
                                                                border: '1px solid rgba(52, 199, 89, 0.15)',
                                                                backdropFilter: 'blur(10px)',
                                                            }}
                                                        >
                                                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                                                <Box sx={{ flex: 1 }}>
                                                                    <Typography
                                                                        variant="subtitle1"
                                                                        sx={{
                                                                            fontWeight: 700,
                                                                            color: '#1d1d1f',
                                                                            fontSize: '16px',
                                                                            mb: 2,
                                                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                                        }}
                                                                    >
                                                                        Assigned Job
                                                                    </Typography>
                                                                    <Box display="flex" flexWrap="wrap" gap={1}>
                                                                        <Chip
                                                                            icon={<Work sx={{ fontSize: 14 }} />}
                                                                            label={`${employeeJobSelections[group.id].jobNumber}`}
                                                                            size="small"
                                                                            sx={{
                                                                                height: '28px',
                                                                                borderRadius: '8px',
                                                                                backgroundColor: 'rgba(52, 199, 89, 0.1)',
                                                                                color: '#34c759',
                                                                                border: '1px solid rgba(52, 199, 89, 0.2)',
                                                                                fontWeight: 600,
                                                                                fontSize: '12px',
                                                                            }}
                                                                        />
                                                                        <Chip
                                                                            icon={<Business sx={{ fontSize: 14 }} />}
                                                                            label={`${employeeJobSelections[group.id].customer}`}
                                                                            size="small"
                                                                            sx={{
                                                                                height: '28px',
                                                                                borderRadius: '8px',
                                                                                backgroundColor: 'rgba(52, 199, 89, 0.1)',
                                                                                color: '#34c759',
                                                                                border: '1px solid rgba(52, 199, 89, 0.2)',
                                                                                fontWeight: 600,
                                                                                fontSize: '12px',
                                                                            }}
                                                                        />
                                                                        <Chip
                                                                            icon={<Build sx={{ fontSize: 14 }} />}
                                                                            label={`${employeeJobSelections[group.id].product}`}
                                                                            size="small"
                                                                            sx={{
                                                                                height: '28px',
                                                                                borderRadius: '8px',
                                                                                backgroundColor: 'rgba(52, 199, 89, 0.1)',
                                                                                color: '#34c759',
                                                                                border: '1px solid rgba(52, 199, 89, 0.2)',
                                                                                fontWeight: 600,
                                                                                fontSize: '12px',
                                                                            }}
                                                                        />
                                                                
                                                                        <Chip
                                                                            icon={<Assignment sx={{ fontSize: 14 }} />}
                                                                            label={`${employeeJobSelections[group.id].mainOperation}`}
                                                                            size="small"
                                                                            sx={{
                                                                                height: '28px',
                                                                                borderRadius: '8px',
                                                                                backgroundColor: 'rgba(0, 122, 255, 0.1)',
                                                                                color: '#007aff',
                                                                                border: '1px solid rgba(0, 122, 255, 0.2)',
                                                                                fontWeight: 600,
                                                                                fontSize: '12px',
                                                                            }}
                                                                        />
                                                                        {isBreakingGroup && (
                                                                            <Tooltip title="Select Number of Ups">
                                                                                <Chip
                                                                                    icon={<PrecisionManufacturing sx={{ fontSize: 14 }} />}
                                                                                    label={employeeJobSelections[group.id].upCount
                                                                                        ? `Ups: ${employeeJobSelections[group.id].upCount}`
                                                                                        : 'Select Ups'}
                                                                                    onClick={(e) => openUpsPopover(e, group.id)}
                                                                                    size="small"
                                                                                    variant="outlined"
                                                                                    sx={{
                                                                                        height: '28px',
                                                                                        borderRadius: '8px',
                                                                                        color: employeeJobSelections[group.id].upCount ? '#1d1d1f' : '#c93400',
                                                                                        borderColor: employeeJobSelections[group.id].upCount
                                                                                            ? 'rgba(0, 0, 0, 0.16)'
                                                                                            : 'rgba(255, 149, 0, 0.55)',
                                                                                        backgroundColor: '#fff',
                                                                                        fontWeight: 700,
                                                                                        fontSize: '12px',
                                                                                        cursor: 'pointer',
                                                                                        '&:hover': {
                                                                                            backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            </Tooltip>
                                                                        )}
                                                                        <Tooltip title="Edit main operation">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={(e) => openEditMainOperation(e, group.id)}
                                                                                sx={{
                                                                                    ml: 0.5,
                                                                                    backgroundColor: 'rgba(0, 122, 255, 0.08)',
                                                                                    color: '#007aff',
                                                                                    borderRadius: '8px',
                                                                                    '&:hover': { backgroundColor: 'rgba(0, 122, 255, 0.15)' }
                                                                                }}
                                                                            >
                                                                                <Settings fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>

                                                                </Box>
                                                                <Tooltip title="Remove Job Assignment">
                                                                    <IconButton
                                                                        onClick={() => {
                                                                            setEmployeeJobSelections((prevSelections) => {
                                                                                const updatedSelections = { ...prevSelections };
                                                                                delete updatedSelections[group.id];
                                                                                return updatedSelections;
                                                                            });
                                                                            setDisabledButtons((prev) => ({ ...prev, [group.id]: false }));
                                                                        }}
                                                                        sx={{
                                                                            backgroundColor: 'rgba(255, 59, 48, 0.1)',
                                                                            color: '#ff3b30',
                                                                            borderRadius: '8px',
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(255, 59, 48, 0.2)',
                                                                                transform: 'scale(1.05)',
                                                                            },
                                                                            transition: 'all 0.2s ease',
                                                                        }}
                                                                        size="small"
                                                                    >
                                                                        <CancelIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        </Paper>
                                                    ) : (


                                                        <Box display="flex" gap={2} flexWrap="wrap">
                                                            <AppleButton
                                                                startIcon={<Assignment />}
                                                                onClick={() => handleOpenPlanDialog(group.id)}
                                                                disabled={disabledButtons[group.id]}
                                                            >
                                                                {machineCode.startsWith("QCTW") ? "QCM Jobs" : "Select from Plan"}
                                                            </AppleButton>

                                                            {!machineCode.startsWith("QCTW") && (
                                                                <AppleButton
                                                                    variant="contained"
                                                                    startIcon={<PrecisionManufacturing />}
                                                                    onClick={() => handleOpenLineupDialog(group.id)}
                                                                    disabled={disabledButtons[group.id]}
                                                                    sx={{
                                                                        background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                                                                        color: 'white',
                                                                        boxShadow: '0 4px 16px rgba(156, 39, 176, 0.3)',
                                                                        '&:hover': {
                                                                            background: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)',
                                                                            boxShadow: '0 6px 20px rgba(156, 39, 176, 0.4)',

                                                                        },
                                                                        '&:disabled': {
                                                                            background: 'rgba(142, 142, 147, 0.3)',
                                                                            color: 'rgba(255, 255, 255, 0.6)',
                                                                            boxShadow: 'none',
                                                                            transform: 'none',
                                                                        }
                                                                    }}
                                                                >
                                                                    Select from Lineup
                                                                </AppleButton>
                                                            )}

                                                            <AppleButton
                                                                variant="outlined"
                                                                startIcon={<Notes />}
                                                                onClick={() => handleOpenManualEntryDialog(group.id)}
                                                                disabled={disabledButtons[group.id]}
                                                            >
                                                                Manual Entry
                                                            </AppleButton>
                                                        </Box>
                                                    )}
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </Stack>
                        </Box>
                    </Fade>
                );

            case 2:
                const totalEmployees = groups.reduce((sum, group) => sum + group.selectedEmployees.length, 0);
                const assignedGroups = Object.keys(employeeJobSelections).length;

                return (
                    <Fade in timeout={500}>
                        <Box sx={{ width: '100%' }}>


                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 3,
                                    borderRadius: '16px',
                                    background: 'linear-gradient(135deg, rgba(0, 122, 255, 0.08) 0%, rgba(88, 86, 214, 0.08) 100%)',
                                    border: '1px solid rgba(0, 122, 255, 0.15)',
                                    backdropFilter: 'blur(10px)',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Box sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}>
                                        <Done sx={{ color: 'white', fontSize: 18 }} />
                                    </Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1d1d1f',
                                            fontSize: '16px',
                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                        }}
                                    >
                                        Summary
                                    </Typography>
                                </Box>
                                <Box display="flex" gap={2} flexWrap="wrap">
                                    <Chip
                                        icon={<Groups />}
                                        label={`${totalEmployees} Employees`}
                                        sx={{
                                            height: '32px',
                                            borderRadius: '8px',
                                            background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            backdropFilter: 'blur(10px)',
                                        }}
                                    />
                                    <Chip
                                        icon={<Assignment />}
                                        label={`${assignedGroups} Groups`}
                                        sx={{
                                            height: '32px',
                                            borderRadius: '8px',
                                            background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            backdropFilter: 'blur(10px)',
                                        }}
                                    />
                                    <Chip
                                        icon={<Work />}
                                        label={`${assignedGroups} Jobs`}
                                        sx={{
                                            height: '32px',
                                            borderRadius: '8px',
                                            background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '13px',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            backdropFilter: 'blur(10px)',
                                        }}
                                    />
                                </Box>
                            </Paper>

                            <Stack spacing={3}>
                                <AnimatePresence>
                                    {groups.map((group, index) => (
                                        <motion.div
                                            key={group.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{
                                                duration: 0.3,
                                                delay: index * 0.1,
                                                type: "spring",
                                                stiffness: 300,
                                                damping: 25
                                            }}
                                        >
                                            <Card
                                                elevation={0}
                                                sx={{
                                                    borderRadius: '20px',
                                                    background: 'rgba(255, 255, 255, 0.9)',
                                                    backdropFilter: 'blur(20px)',
                                                    border: '2px solid rgba(0, 122, 255, 0.1)',
                                                    transition: 'all 0.3s ease',

                                                }}
                                            >
                                                <CardContent sx={{ p: 3 }}>
                                                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                                                        <Box sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: '12px',
                                                            background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            boxShadow: '0 4px 12px rgba(0, 122, 255, 0.25)',
                                                        }}>
                                                            <Groups sx={{ color: 'white', fontSize: 20 }} />
                                                        </Box>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 700,
                                                                color: '#1d1d1f',
                                                                fontSize: '18px',
                                                                letterSpacing: '-0.2px',
                                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                            }}
                                                        >
                                                            Group {group.id}
                                                        </Typography>
                                                    </Box>

                                                    <Box mb={2}>
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                color: '#8e8e93',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                                mb: 1,
                                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                            }}
                                                        >
                                                            Team Members:
                                                        </Typography>
                                                        <List dense sx={{ py: 0 }}>
                                                            {group.selectedEmployees.map(emp => (
                                                                <ListItem key={emp.emp_id} sx={{ py: 0.5, px: 0 }}>
                                                                    <ListItemAvatar>
                                                                        <Avatar sx={{
                                                                            width: 28,
                                                                            height: 28,
                                                                            fontSize: '12px',
                                                                            fontWeight: 600,
                                                                            bgcolor: '#007aff',
                                                                        }}>
                                                                            {emp.emp_name.charAt(0)}
                                                                        </Avatar>
                                                                    </ListItemAvatar>
                                                                    <ListItemText
                                                                        primary={
                                                                            <Typography
                                                                                variant="body2"
                                                                                sx={{
                                                                                    fontWeight: 600,
                                                                                    color: '#1d1d1f',
                                                                                    fontSize: '14px',
                                                                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                                                }}
                                                                            >
                                                                                {emp.emp_name}
                                                                            </Typography>
                                                                        }
                                                                        secondary={
                                                                            <Typography
                                                                                variant="caption"
                                                                                sx={{
                                                                                    color: '#8e8e93',
                                                                                    fontSize: '12px',
                                                                                    fontWeight: 500,
                                                                                }}
                                                                            >
                                                                                ID: {emp.emp_id}
                                                                            </Typography>
                                                                        }
                                                                    />
                                                                </ListItem>
                                                            ))}
                                                        </List>
                                                    </Box>

                                                    <Divider sx={{ my: 2, borderColor: 'rgba(0, 0, 0, 0.06)' }} />

                                                    <Box>
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{
                                                                color: '#8e8e93',
                                                                fontSize: '14px',
                                                                fontWeight: 600,
                                                                mb: 1,
                                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                            }}
                                                        >
                                                            Assigned Job:
                                                        </Typography>
                                                        {employeeJobSelections[group.id] ? (
                                                            <Box display="flex" flexWrap="wrap" gap={1}>
                                                                <Chip
                                                                    icon={<Work sx={{ fontSize: 14 }} />}
                                                                    label={`${employeeJobSelections[group.id].jobNumber}`}
                                                                    sx={{
                                                                        height: '28px',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: 'rgba(0, 122, 255, 0.1)',
                                                                        color: '#007aff',
                                                                        border: '1px solid rgba(0, 122, 255, 0.2)',
                                                                        fontWeight: 600,
                                                                        fontSize: '12px',
                                                                    }}
                                                                />
                                                                <Chip
                                                                    icon={<Business sx={{ fontSize: 14 }} />}
                                                                    label={`${employeeJobSelections[group.id].customer}`}
                                                                    sx={{
                                                                        height: '28px',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: 'rgba(0, 122, 255, 0.1)',
                                                                        color: '#007aff',
                                                                        border: '1px solid rgba(0, 122, 255, 0.2)',
                                                                        fontWeight: 600,
                                                                        fontSize: '12px',
                                                                    }}
                                                                />
                                                                <Chip
                                                                    icon={<Build sx={{ fontSize: 14 }} />}
                                                                    label={`${employeeJobSelections[group.id].product}`}
                                                                    sx={{
                                                                        height: '28px',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: 'rgba(0, 122, 255, 0.1)',
                                                                        color: '#007aff',
                                                                        border: '1px solid rgba(0, 122, 255, 0.2)',
                                                                        fontWeight: 600,
                                                                        fontSize: '12px',
                                                                    }}
                                                                />
                                                                <Chip
                                                                    icon={<Assignment sx={{ fontSize: 14 }} />}
                                                                    label={`${employeeJobSelections[group.id].mainOperation}`}
                                                                    sx={{
                                                                        height: '28px',
                                                                        borderRadius: '8px',
                                                                        backgroundColor: 'rgba(0, 122, 255, 0.1)',
                                                                        color: '#007aff',
                                                                        border: '1px solid rgba(0, 122, 255, 0.2)',
                                                                        fontWeight: 600,
                                                                        fontSize: '12px',
                                                                    }}
                                                                />
                                                                        {isBreakingGroup && employeeJobSelections[group.id].upCount && (
                                                                            <Chip
                                                                                icon={<PrecisionManufacturing sx={{ fontSize: 14 }} />}
                                                                                label={`Ups: ${employeeJobSelections[group.id].upCount}`}
                                                                                size="small"
                                                                                sx={{
                                                                                    height: '28px',
                                                                                    borderRadius: '8px',
                                                                                    backgroundColor: 'rgba(255, 149, 0, 0.12)',
                                                                                    color: '#c93400',
                                                                                    border: '1px solid rgba(255, 149, 0, 0.25)',
                                                                                    fontWeight: 700,
                                                                                    fontSize: '12px',
                                                                                }}
                                                                            />
                                                                        )}

                                                            </Box>
                                                        ) : (
                                                            <Paper
                                                                elevation={0}
                                                                sx={{
                                                                    p: 2,
                                                                    borderRadius: '12px',
                                                                    background: 'linear-gradient(135deg, rgba(255, 149, 0, 0.08) 0%, rgba(255, 189, 46, 0.08) 100%)',
                                                                    border: '1px solid rgba(255, 149, 0, 0.15)',
                                                                    backdropFilter: 'blur(10px)',
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{
                                                                        color: '#ff9500',
                                                                        fontSize: '14px',
                                                                        fontWeight: 600,
                                                                        textAlign: 'center',
                                                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                                    }}
                                                                >
                                                                    No job assigned
                                                                </Typography>
                                                            </Paper>
                                                        )}
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </motion.div>
                                    ))}
                                </AnimatePresence>
                            </Stack>
                        </Box>
                    </Fade>
                );
            default:
                return <Typography>Unknown step</Typography>;
        }
    };

    return (
        <Box sx={{ margin: 2 }}>
            <Tooltip title="Add New Job" arrow>
                <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                    <Button
                        variant="contained"
                        onClick={handleAddToJobClick}
                        disabled={loading}
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            minWidth: 0,
                            background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                            boxShadow: '0 8px 32px rgba(0, 122, 255, 0.3)',
                            border: '3px solid rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                                boxShadow: '0 12px 40px rgba(0, 122, 255, 0.4)',
                                background: 'linear-gradient(135deg, #0056cc 0%, #4c44cc 100%)',
                            },
                            '&:disabled': {
                                background: 'rgba(142, 142, 147, 0.3)',
                                boxShadow: '0 4px 16px rgba(142, 142, 147, 0.2)',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={28} sx={{ color: 'white' }} />
                        ) : (
                            <AddSharp sx={{ fontSize: 28, color: 'white' }} />
                        )}
                    </Button>
                </motion.div>
            </Tooltip>

            {/* Main Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                        setDialogOpen(false);
                    }
                }}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(40px) saturate(180%)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)',
                        minHeight: '70vh',
                        overflow: 'hidden',
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(8px)',
                    }
                }}
                TransitionComponent={Zoom}
                transitionDuration={400}
            >

                <DialogTitle
                    sx={{
                        px: 3,
                        py: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '18px',
                        letterSpacing: '-0.3px',
                        borderBottom: '1px solid rgba(255,255,255,0.2)',
                    }}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 700,
                                fontSize: '18px',
                                lineHeight: 1.2,
                            }}
                        >
                            Job Assignment
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'rgba(255,255,255,0.8)',
                                fontSize: '13px',
                                mt: 0.3,
                            }}
                        >
                            Review assignments before starting
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={handleCloseDialog}
                        sx={{
                            color: 'rgba(255,255,255,0.8)',
                            '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                        }}
                        size="small"
                    >
                        <Close fontSize="small" />
                    </IconButton>
                </DialogTitle>


                <Box sx={{
                    px: 4,
                    py: 2,
                    background: 'rgba(248, 249, 250, 0.8)',
                    backdropFilter: 'blur(20px)',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
                }}>
                    <Stepper activeStep={activeStep} alternativeLabel>
                        {steps.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel
                                    icon={
                                        <Box sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            background: index === activeStep
                                                ? 'linear-gradient(135deg, #007aff 0%, #5856d6 100%)'
                                                : index < activeStep
                                                    ? 'linear-gradient(135deg, #34c759 0%, #30d158 100%)'
                                                    : 'rgba(142, 142, 147, 0.3)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: 16,
                                            fontWeight: 600,
                                            transition: 'all 0.3s ease',
                                        }}>
                                            {index < activeStep ? (
                                                <CheckCircle sx={{ fontSize: 18 }} />
                                            ) : (
                                                step.icon
                                            )}
                                        </Box>
                                    }
                                    sx={{
                                        '& .MuiStepLabel-label': {
                                            fontWeight: 600,
                                            color: index === activeStep ? '#007aff' : '#8e8e93',
                                            fontSize: '14px',
                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                        }
                                    }}
                                >
                                    {step.label}
                                </StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <LinearProgress
                        variant="determinate"
                        value={(activeStep / (steps.length - 1)) * 100}
                        sx={{
                            mt: 2,
                            borderRadius: 2,
                            height: 6,
                            backgroundColor: 'rgba(0, 122, 255, 0.1)',
                            '& .MuiLinearProgress-bar': {
                                background: 'linear-gradient(90deg, #007aff 0%, #5856d6 100%)',
                                borderRadius: 2,
                            }
                        }}
                    />
                </Box>

                <DialogContent sx={{ px: 4, py: 4, background: 'rgba(248, 249, 250, 0.8)', backdropFilter: 'blur(20px)' }}>
                    {renderStepContent(activeStep)}
                </DialogContent>

                <DialogActions sx={{
                    px: 4,
                    py: 3,
                    gap: 2,
                    background: 'rgba(248, 249, 250, 0.8)',
                    backdropFilter: 'blur(20px)',
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                }}>
                    <AppleButton
                        onClick={handleCloseDialog}
                        variant="outlined"
                        sx={{ minWidth: '100px' }}
                    >
                        Close
                    </AppleButton>

                    <Box sx={{ flex: 1 }} />

                    {activeStep === steps.length - 1 ? (
                        <>
                            <AppleButton
                                onClick={handleReset}
                                variant="outlined"
                                sx={{ minWidth: '100px' }}
                            >
                                Reset
                            </AppleButton>
                            <AppleButton
                                onClick={handleConfirm}
                                color="success"
                                disabled={submitting}
                                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                                sx={{ minWidth: '140px' }}
                            >
                                {submitting ? 'Starting...' : 'Start Jobs'}
                            </AppleButton>
                        </>
                    ) : (
                        <>
                            <AppleButton
                                onClick={handleBack}
                                disabled={activeStep === 0}
                                variant="outlined"
                                sx={{ minWidth: '100px' }}
                            >
                                Back
                            </AppleButton>
                            <AppleButton
                                onClick={handleNext}
                                sx={{ minWidth: '100px' }}
                            >
                                Next
                            </AppleButton>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Plan Selection Dialog */}
            <Dialog
                open={planDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                        handleClosePlanDialog();
                    }
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(40px) saturate(180%)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)',
                        overflow: 'hidden',
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(8px)',
                    }
                }}
                TransitionComponent={Zoom}
                transitionDuration={400}
            >
                <AppleDialogHeader
                    title={machineCode.startsWith("QCTW") ? "QC Completed Jobs" : "Select Job From Plan"}
                    subtitle="Choose a job from available options"
                    icon={Assignment}
                    onClose={handleClosePlanDialog}
                />

                <DialogContent sx={{ p: 4, background: 'rgba(248, 249, 250, 0.8)', backdropFilter: 'blur(20px)' }}>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <Autocomplete
                            options={categorizedJobs}
                            loading={fetchingJobs}
                            groupBy={(option) => {
                                if (machineCode.startsWith("QCTW")) {
                                    const endDate = new Date(option.job?.endDatetime).toLocaleDateString();
                                    return `${endDate} - ${option.job?.machineCode || "Unknown Machine"}`;
                                } else {
                                    return option.label || "No Date";
                                }
                            }}
                            getOptionLabel={(option) =>
                                option?.job?.jobNumber
                                    ? `${option.job.jobNumber} - ${option.job.customer} - ${option.job.WOdescription} - ${option.job.OpDesctiption}`
                                    : "No Job Information"
                            }
                            onOpen={async () => {
                                if (categorizedJobs.length === 0) {
                                    setFetchingJobs(true);
                                    try {
                                        await fetchJobData();
                                    } catch (error) {
                                        console.error("Error fetching job data:", error);
                                        onTriggerSnackbar('Failed to load job data. Please try again.', 'error');
                                    } finally {
                                        setFetchingJobs(false);
                                    }
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search and Select Job"
                                    fullWidth
                                    variant="outlined"
                                    placeholder="Type to search or browse available jobs"
                                    helperText={fetchingJobs ? "Loading jobs..." : "Click to load available jobs"}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '16px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                            backdropFilter: 'blur(20px)',
                                            border: '2px solid rgba(0, 0, 0, 0.04)',
                                            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '& fieldset': {
                                                border: 'none',
                                            },
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.95)',

                                                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: 'rgba(255, 255, 255, 1)',

                                                boxShadow: '0 8px 25px rgba(0, 122, 255, 0.15)',
                                                border: '2px solid rgba(0, 122, 255, 0.3)',
                                            },
                                        },
                                    }}
                                />
                            )}
                            onChange={(event, value) => handleEmployeeJobSelect(selectedGroupId, value?.job)}
                            value={categorizedJobs.find(option => option.job === selectedJob) || null}
                            renderOption={(props, option) => (
                                <li {...props} style={{
                                    padding: '12px 16px',
                                    borderRadius: '8px',
                                    margin: '4px 8px',
                                    transition: 'all 0.2s ease',
                                }}>
                                    <Box sx={{ width: '100%' }}>
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1d1d1f',
                                                fontSize: '14px',
                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                            }}
                                        >
                                            {option?.job?.jobNumber || "N/A"} - {option?.job?.customer || "N/A"}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#8e8e93',
                                                fontSize: '12px',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {option?.job?.WOdescription || "N/A"} • {option?.job?.OpDesctiption || "N/A"}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            isOptionEqualToValue={(option, value) => option.job?.jobNumber === value?.job?.jobNumber}
                            clearOnEscape
                            disableClearable={false}
                            sx={{
                                '& .MuiAutocomplete-listbox': {
                                    borderRadius: '16px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                    backdropFilter: 'blur(20px)',
                                    border: '1px solid rgba(0, 0, 0, 0.08)',
                                    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.12)',
                                },
                            }}
                        />


                    </Stack>
                </DialogContent>

                <DialogActions sx={{
                    p: 4,
                    gap: 3,
                    background: 'rgba(248, 249, 250, 0.8)',
                    backdropFilter: 'blur(20px)',
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                }}>
                    <AppleButton
                        onClick={handleClosePlanDialog}
                        variant="outlined"
                        sx={{ minWidth: '120px' }}
                    >
                        Cancel
                    </AppleButton>

                    <AppleButton
                        onClick={() => {
                            if (!selectedJob) return;
                            handleConfirmJobData(selectedGroupId, {
                                jobNumber: selectedJob.jobNumber || '',
                                customer: selectedJob.customer || '',
                                product: selectedJob.WOdescription || '',
                                mainOperation: selectedJob.OpDesctiption || '', // default from job data
                            });
                        }}
                        disabled={!selectedJob || !selectedJob.jobNumber}
                    >
                        Confirm Selection
                    </AppleButton>
                </DialogActions>
            </Dialog>


            {/* Lineup Selection Dialog */}

            <Dialog
                open={lineupDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                        handleCloseLineupDialog();
                    }
                }}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(40px) saturate(180%)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)',
                        overflow: 'hidden',
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(8px)',
                    }
                }}
                TransitionComponent={Zoom}
                transitionDuration={400}
            >
                <AppleDialogHeader
                    title="Select Job From Lineup"
                    subtitle="Choose a job from the lineup queue"
                    icon={PrecisionManufacturing}
                    color="linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)"
                    onClose={handleCloseLineupDialog}
                />

                <DialogContent sx={{ p: 4, background: 'rgba(248, 249, 250, 0.8)', backdropFilter: 'blur(20px)' }}>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Autocomplete
                                sx={{ flex: 1 }} // Take most of the space
                                options={categorizedJobs}
                                loading={fetchingLineupJobs}
                                getOptionLabel={(option) =>
                                    option?.job?.jobNumber
                                        ? `${option.job.jobNumber} - ${option.job.customer} - ${option.job.WOdescription} - ${option.job.OpDesctiption}`
                                        : "No Job Information"
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search and Select Lineup Job"
                                        fullWidth
                                        variant="outlined"
                                        placeholder="Type to search or browse available lineup jobs"
                                        helperText={fetchingLineupJobs ? "Refreshing lineup jobs..." : "Click to refresh lineup jobs........................."}
                                        onClick={async (e) => {
                                            // Your existing onClick logic here
                                            if (!fetchingLineupJobs) {
                                                setFetchingLineupJobs(true);
                                                try {
                                                    onTriggerSnackbar('🔄 Refreshing lineup jobs...', 'info');
                                                    setCategorizedJobs([]);
                                                    setSelectedJob(null);
                                                    await fetchLineupJobs();
                                                    onTriggerSnackbar('✅ Lineup jobs updated!', 'success');
                                                } catch (error) {
                                                    console.error("Error fetching lineup jobs:", error);
                                                    onTriggerSnackbar('❌ Failed to refresh lineup jobs.', 'error');
                                                } finally {
                                                    setFetchingLineupJobs(false);
                                                }
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '16px',
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                backdropFilter: 'blur(20px)',
                                                border: '2px solid rgba(0, 0, 0, 0.04)',
                                                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                cursor: 'pointer',
                                                '& fieldset': {
                                                    border: 'none',
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',

                                                    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
                                                },
                                                '&.Mui-focused': {
                                                    backgroundColor: 'rgba(255, 255, 255, 1)',

                                                    boxShadow: '0 8px 25px rgba(156, 39, 176, 0.15)',
                                                    border: '2px solid rgba(156, 39, 176, 0.3)',
                                                },
                                            },
                                            // Special styling when loading
                                            ...(fetchingLineupJobs && {
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'rgba(156, 39, 176, 0.05)',
                                                    border: '2px solid rgba(156, 39, 176, 0.2)',
                                                }
                                            })
                                        }}
                                    />
                                )}
                                onChange={(event, value) => handleEmployeeJobSelect(selectedGroupId, value?.job)}
                                value={categorizedJobs.find(option => option.job === selectedJob) || null}
                                renderOption={(props, option) => (
                                    <li {...props} style={{
                                        padding: '12px 16px',
                                        borderRadius: '8px',
                                        margin: '4px 8px',
                                        transition: 'all 0.2s ease',
                                    }}>
                                        <Box sx={{ width: '100%' }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontWeight: 600,
                                                    color: '#1d1d1f',
                                                    fontSize: '14px',
                                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                }}
                                            >
                                                {option?.job?.jobNumber || "N/A"} - {option?.job?.customer || "N/A"}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: '#8e8e93',
                                                    fontSize: '12px',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {option?.job?.WOdescription || "N/A"} • {option?.job?.OpDesctiption || "N/A"}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: '#9c27b0',
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Lineup: {option.label}
                                            </Typography>
                                        </Box>
                                    </li>
                                )}
                                isOptionEqualToValue={(option, value) => option.job?.jobNumber === value?.job?.jobNumber}
                                clearOnEscape
                                disableClearable={false}

                            />

                            {/* OPTIONAL: Refresh Button */}
                            <Tooltip title="Refresh lineup jobs">
                                <IconButton
                                    onClick={async () => {
                                        if (!fetchingLineupJobs) {
                                            setFetchingLineupJobs(true);
                                            try {
                                                onTriggerSnackbar('🔄 Refreshing lineup jobs...', 'info');
                                                setCategorizedJobs([]);
                                                setSelectedJob(null);
                                                await fetchLineupJobs();
                                                onTriggerSnackbar('✅ Lineup jobs refreshed!', 'success');
                                            } catch (error) {
                                                console.error("Error refreshing lineup jobs:", error);
                                                onTriggerSnackbar('❌ Failed to refresh lineup jobs.', 'error');
                                            } finally {
                                                setFetchingLineupJobs(false);
                                            }
                                        }
                                    }}
                                    disabled={fetchingLineupJobs}
                                    sx={{
                                        mt: 1, // Align with TextField
                                        width: 48,
                                        height: 48,
                                        backgroundColor: 'rgba(156, 39, 176, 0.1)',
                                        color: '#9c27b0',
                                        borderRadius: '12px',
                                        '&:hover': {
                                            backgroundColor: 'rgba(156, 39, 176, 0.2)',
                                            transform: 'scale(1.05)',
                                        },
                                        '&:disabled': {
                                            backgroundColor: 'rgba(142, 142, 147, 0.1)',
                                            color: 'rgba(142, 142, 147, 0.5)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {fetchingLineupJobs ? (
                                        <CircularProgress size={20} sx={{ color: '#9c27b0' }} />
                                    ) : (
                                        <Refresh sx={{ fontSize: 20 }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{
                    p: 4,
                    gap: 3,
                    background: 'rgba(248, 249, 250, 0.8)',
                    backdropFilter: 'blur(20px)',
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                }}>
                    <AppleButton
                        onClick={handleCloseLineupDialog}
                        variant="outlined"
                        sx={{ minWidth: '120px' }}
                    >
                        Cancel
                    </AppleButton>

                    <AppleButton
                        onClick={() => {
                            if (!selectedJob) return;
                            handleConfirmJobData(selectedGroupId, {
                                jobNumber: selectedJob.jobNumber || '',
                                customer: selectedJob.customer || '',
                                product: selectedJob.WOdescription || '',
                                mainOperation: selectedJob.OpDesctiption || '',
                                OpDesctiption: selectedJob.OpDesctiption || '', // helps normalization
                                WOdescription: selectedJob.WOdescription || '',
                            });
                        }}
                        disabled={!selectedJob || !selectedJob.jobNumber}
                        sx={{
                            minWidth: '160px',
                            background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)' }
                        }}
                    >
                        Confirm Selection
                    </AppleButton>

                </DialogActions>
            </Dialog>


            {/* Enhanced Manual Entry Dialog */}
            <Dialog
                open={manualEntryDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                        handleCloseManualEntryDialog();
                    }
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(40px) saturate(180%)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12), 0 8px 16px rgba(0, 0, 0, 0.08)',
                        overflow: 'hidden',
                    }
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.4)',
                        backdropFilter: 'blur(8px)',
                    }
                }}
                TransitionComponent={Zoom}
                transitionDuration={400}
            >
                <AppleDialogHeader
                    title="Manual Job Entry"
                    subtitle="Enter job details manually when not available in planned jobs"
                    icon={Notes}
                    color="linear-gradient(135deg, #ff9500 0%, #ffbd2e 100%)"
                    onClose={handleCloseManualEntryDialog}
                />

                <DialogContent sx={{ p: 4, background: 'rgba(248, 249, 250, 0.8)', backdropFilter: 'blur(20px)' }}>
                    <Stack spacing={3}>
                        {/* Enhanced Job Number Field */}
                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#1d1d1f',
                                    mb: 1,
                                    fontSize: '14px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                }}
                            >
                                Job Number *
                            </Typography>
                            <TextField
                                fullWidth
                                required
                                value={manualEntryData.jobNumber}
                                onChange={handleJobNumberChange}
                                onBlur={(e) => validateManualEntryField('jobNumber', e.target.value)}
                                error={!!manualEntryErrors.jobNumber}
                                helperText={
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        width: '100%'
                                    }}>
                                        <span style={{
                                            color: manualEntryErrors.jobNumber ? '#ff3b30' : '#8e8e93'
                                        }}>
                                            {manualEntryErrors.jobNumber || 'Format: NN-NN-XXXX (e.g., 11-22-1234)'}
                                        </span>
                                        <span style={{
                                            color: '#8e8e93',
                                            marginLeft: 'auto'
                                        }}>
                                            {manualEntryData.jobNumber.length}/11
                                        </span>
                                    </Box>
                                }
                                inputProps={{ maxLength: 11 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Work sx={{ color: '#8e8e93', fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: manualEntryData.jobNumber ? (
                                        <InputAdornment position="end">
                                            <Tooltip title="Clear job number">
                                                <IconButton
                                                    onClick={clearJobNumber}
                                                    edge="end"
                                                    size="small"
                                                    sx={{
                                                        color: '#8e8e93',
                                                        '&:hover': {
                                                            color: '#ff3b30',
                                                            backgroundColor: 'rgba(255, 59, 48, 0.1)',
                                                        },
                                                        transition: 'all 0.2s ease',
                                                    }}
                                                >
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ) : null,
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '16px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        backdropFilter: 'blur(20px)',
                                        border: '2px solid rgba(0, 0, 0, 0.04)',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',

                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(255, 255, 255, 1)',

                                            boxShadow: '0 4px 12px rgba(0, 122, 255, 0.12)',
                                            border: '2px solid rgba(0, 122, 255, 0.3)',
                                        },
                                        '&.Mui-error': {
                                            border: '2px solid rgba(255, 59, 48, 0.3)',
                                            '&:hover': {
                                                border: '2px solid rgba(255, 59, 48, 0.4)',
                                            },
                                        },
                                    },
                                }}
                                variant="outlined"
                            />
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#1d1d1f',
                                    mb: 1,
                                    fontSize: '14px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                }}
                            >
                                Customer *
                            </Typography>
                            <TextField
                                fullWidth
                                required
                                value={manualEntryData.customer}
                                onChange={(e) => handleManualEntryChange('customer', e.target.value)}
                                onBlur={(e) => validateManualEntryField('customer', e.target.value)}
                                error={!!manualEntryErrors.customer}
                                helperText={manualEntryErrors.customer}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Business sx={{ color: '#8e8e93', fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '16px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        backdropFilter: 'blur(20px)',
                                        border: '2px solid rgba(0, 0, 0, 0.04)',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',

                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(255, 255, 255, 1)',

                                            boxShadow: '0 4px 12px rgba(0, 122, 255, 0.12)',
                                            border: '2px solid rgba(0, 122, 255, 0.3)',
                                        },
                                        '&.Mui-error': {
                                            border: '2px solid rgba(255, 59, 48, 0.3)',
                                            '&:hover': {
                                                border: '2px solid rgba(255, 59, 48, 0.4)',
                                            },
                                        },
                                    },
                                }}
                                variant="outlined"
                            />
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#1d1d1f',
                                    mb: 1,
                                    fontSize: '14px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                }}
                            >
                                Product *
                            </Typography>
                            <TextField
                                fullWidth
                                required
                                value={manualEntryData.product}
                                onChange={(e) => handleManualEntryChange('product', e.target.value)}
                                onBlur={(e) => validateManualEntryField('product', e.target.value)}
                                error={!!manualEntryErrors.product}
                                helperText={manualEntryErrors.product}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Build sx={{ color: '#8e8e93', fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '16px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        backdropFilter: 'blur(20px)',
                                        border: '2px solid rgba(0, 0, 0, 0.04)',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',

                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(255, 255, 255, 1)',

                                            boxShadow: '0 4px 12px rgba(0, 122, 255, 0.12)',
                                            border: '2px solid rgba(0, 122, 255, 0.3)',
                                        },
                                        '&.Mui-error': {
                                            border: '2px solid rgba(255, 59, 48, 0.3)',
                                            '&:hover': {
                                                border: '2px solid rgba(255, 59, 48, 0.4)',
                                            },
                                        },
                                    },
                                }}
                                variant="outlined"
                            />
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#1d1d1f',
                                    mb: 1,
                                    fontSize: '14px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                }}
                            >
                                Main Operation *
                            </Typography>
                            <TextField
                                fullWidth
                                required
                                value={manualEntryData.mainOperation}
                                onChange={(e) => handleManualEntryChange('mainOperation', e.target.value)}
                                onBlur={(e) => validateManualEntryField('mainOperation', e.target.value)}
                                error={!!manualEntryErrors.mainOperation}
                                helperText={manualEntryErrors.mainOperation}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Assignment sx={{ color: '#8e8e93', fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '16px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        backdropFilter: 'blur(20px)',
                                        border: '2px solid rgba(0, 0, 0, 0.04)',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',

                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(255, 255, 255, 1)',

                                            boxShadow: '0 4px 12px rgba(0, 122, 255, 0.12)',
                                            border: '2px solid rgba(0, 122, 255, 0.3)',
                                        },
                                        '&.Mui-error': {
                                            border: '2px solid rgba(255, 59, 48, 0.3)',
                                            '&:hover': {
                                                border: '2px solid rgba(255, 59, 48, 0.4)',
                                            },
                                        },
                                    },
                                }}
                                variant="outlined"
                            />
                        </Box>

                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{
                                    fontWeight: 600,
                                    color: '#1d1d1f',
                                    mb: 1,
                                    fontSize: '14px',
                                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                }}
                            >
                                Reason for Manual Entry *
                            </Typography>
                            <TextField
                                fullWidth
                                required
                                multiline
                                rows={3}
                                value={manualEntryData.reason}
                                onChange={(e) => handleManualEntryChange('reason', e.target.value)}
                                onBlur={(e) => validateManualEntryField('reason', e.target.value)}
                                error={!!manualEntryErrors.reason}
                                helperText={manualEntryErrors.reason || "Explain why this job is being entered manually"}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                                            <Notes sx={{ color: '#8e8e93', fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '16px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                        backdropFilter: 'blur(20px)',
                                        border: '2px solid rgba(0, 0, 0, 0.04)',
                                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '& fieldset': {
                                            border: 'none',
                                        },
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',

                                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'rgba(255, 255, 255, 1)',

                                            boxShadow: '0 4px 12px rgba(0, 122, 255, 0.12)',
                                            border: '2px solid rgba(0, 122, 255, 0.3)',
                                        },
                                        '&.Mui-error': {
                                            border: '2px solid rgba(255, 59, 48, 0.3)',
                                            '&:hover': {
                                                border: '2px solid rgba(255, 59, 48, 0.4)',
                                            },
                                        },
                                    },
                                }}
                                variant="outlined"
                            />
                        </Box>
                    </Stack>
                </DialogContent>

                <DialogActions sx={{
                    p: 4,
                    gap: 3,
                    background: 'rgba(248, 249, 250, 0.8)',
                    backdropFilter: 'blur(20px)',
                    borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                }}>
                    <AppleButton
                        onClick={handleCloseManualEntryDialog}
                        variant="outlined"
                        sx={{ minWidth: '120px' }}
                    >
                        Cancel
                    </AppleButton>
                    <AppleButton
                        onClick={handleSubmitManualEntry}
                        disabled={!isManualEntryValid()}
                        color="warning"
                        sx={{ minWidth: '140px' }}
                    >
                        Add Job
                    </AppleButton>
                </DialogActions>
            </Dialog>


            <Popover
                open={Boolean(upsAnchorEl)}
                anchorEl={upsAnchorEl}
                onClose={closeUpsPopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        p: 2,
                        width: 240,
                        borderRadius: 2,
                        background: 'rgba(255,255,255,0.98)',
                        border: '1px solid rgba(0,0,0,0.08)',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                    }
                }}
            >
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
                    Number of Ups
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mb: 1.5, color: '#6e6e73' }}>
                    Select a value from 1 to 50
                </Typography>
                <FormControl fullWidth size="small" required>
                    <InputLabel>Number of Ups</InputLabel>
                    <Select
                        label="Number of Ups"
                        value={upsGroupId ? (employeeJobSelections[upsGroupId]?.upCount ?? '') : ''}
                        onChange={(e) => selectUpCount(e.target.value)}
                        autoFocus
                        sx={{ borderRadius: 2, backgroundColor: '#fff' }}
                    >
                        {UPS_OPTIONS.map(value => (
                            <MenuItem key={value} value={value}>{value}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Popover>

            <Popover
                open={Boolean(editAnchorEl)}
                anchorEl={editAnchorEl}
                onClose={closeEditMainOperation}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}


                PaperProps={{
                    sx: {
                        p: 2,
                        borderRadius: 2,
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(0,0,0,0.06)',
                        width: 320,
                    }
                }}
            >
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    Select Main Operation
                </Typography>

                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Main Operation</InputLabel>
                    <Select
                        label="Main Operation"
                        value={tempMainOperation}
                        onChange={(e) => setTempMainOperation(e.target.value)}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            '& .MuiOutlinedInput-notchedOutline': { border: '2px solid rgba(0,0,0,0.04)' },
                            '&:hover .MuiOutlinedInput-notchedOutline': { border: '2px solid rgba(0,122,255,0.2)' },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: '2px solid rgba(0,122,255,0.3)' },
                        }}
                    >
                        {loadingMainOps ? (
                            <MenuItem disabled>Loading...</MenuItem>
                        ) : (
                            mainOps.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)
                        )}
                    </Select>
                </FormControl>

                <Box display="flex" gap={1} justifyContent="flex-end">
                    <AppleButton variant="outlined" onClick={closeEditMainOperation}>Cancel</AppleButton>
                    <AppleButton onClick={saveEditMainOperation}>Save</AppleButton>
                </Box>
            </Popover>
        </Box>
    );
};

JobAddButton.propTypes = {
    supervisorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    machineCode: PropTypes.string.isRequired,
    groupCode: PropTypes.string.isRequired,
    onAddJob: PropTypes.func,
    onTriggerSnackbar: PropTypes.func.isRequired,
};

export default JobAddButton;